import type { ReactNode } from 'react';

/**
 * Every glyph in the app.
 *
 * There is no icon package here and a couple of dozen shapes does not justify
 * adding one. What did not work was the previous arrangement: this factory
 * existed three times over -- here, privately inside Layout, and again inside
 * Projects -- so the one thing that makes hand-drawn icons look like a set,
 * a single stroke spec, was three copies waiting to drift apart.
 *
 * The spec is: a 24x24 box, 1.5 stroke, round caps and joins, `currentColor`,
 * and `h-4 w-4` unless the caller says otherwise. Anything new belongs here and
 * belongs to that spec.
 */
function Icon({ children, className = 'h-4 w-4' }: { children: ReactNode; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={className}
    >
      {children}
    </svg>
  );
}

export const PlusIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const CloseIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

export const CheckIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="m20 6-11 11-5-5" />
  </Icon>
);

/** A checklist: two ticks and the lines they belong to. Drawn to stay distinct
 *  at 12px from SubtaskIcon's branch and from the bare CheckIcon beside it on
 *  the row's meta strip. */
export const ChecklistIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="m3 6 1.5 1.5L7.5 4.5M3 15l1.5 1.5L7.5 13.5M11 6h10M11 15h10" />
  </Icon>
);

/**
 * A session's mark: what you picked to work on now.
 *
 * Two rings and a centre rather than a star or a flag, because both of those
 * already mean "important" everywhere else in software -- and a focus session is
 * not about importance. An urgent task you are not doing this hour does not
 * belong in it.
 *
 * `filled` is the picked state, drawn as a solid centre rather than a different
 * glyph so the two states are the same shape at a glance.
 */
export const TargetIcon = ({ className, filled = false }: { className?: string; filled?: boolean }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3.5" fill={filled ? 'currentColor' : 'none'} />
  </Icon>
);

/** Back onto the pile -- an arrow turning away, the mirror of picking one up. */
export const UndoIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h11a5 5 0 0 1 0 10H9" />
  </Icon>
);

export const PlayIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M6 4.5v15l13-7.5-13-7.5Z" />
  </Icon>
);

export const StopIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <rect x="6" y="6" width="12" height="12" rx="1.5" />
  </Icon>
);

export const ClockIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Icon>
);

export const CommentIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5Z" />
  </Icon>
);

export const PaperclipIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M18 8.5 9.7 16.8a3 3 0 0 1-4.2-4.2l8.5-8.5a4.5 4.5 0 0 1 6.4 6.4l-8.5 8.5a6 6 0 0 1-8.5-8.5l7.6-7.6" />
  </Icon>
);

export const TrashIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 7h16M10 7V5h4v2M6 7l1 13h10l1-13" />
  </Icon>
);

export const BlockedIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="m5.6 5.6 12.8 12.8" />
  </Icon>
);

export const ExpandIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M14 4h6v6M20 4l-7 7M10 20H4v-6M4 20l7-7" />
  </Icon>
);

export const SubtaskIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M7 4v9a3 3 0 0 0 3 3h7" />
    <path d="m14 13 3 3-3 3" />
  </Icon>
);

/* The property-sheet label column. One per row, so the labels read as a column
   of properties rather than a column of words. */

export const StatusIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5a4.5 4.5 0 0 1 0 9Z" fill="currentColor" stroke="none" />
  </Icon>
);

export const PriorityIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M5 21V4M5 4h11l-2 3.5L16 11H5" />
  </Icon>
);

export const UserIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </Icon>
);

export const UsersIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="9" cy="8" r="3.5" />
    <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
    <path d="M16 5.2a3.5 3.5 0 0 1 0 5.6M17.5 14.4a6.5 6.5 0 0 1 4 5.6" />
  </Icon>
);

export const CalendarIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <rect x="3.5" y="5" width="17" height="15" rx="2" />
    <path d="M3.5 10h17M8 3.5v3M16 3.5v3" />
  </Icon>
);

/** A bell, for a reminder. Next to CalendarIcon above because the property it
 *  labels sits next to the dates -- a moment rather than a day. */
export const BellIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M6 9a6 6 0 0 1 12 0c0 3.5.8 5.3 1.7 6.3a.7.7 0 0 1-.5 1.2H4.8a.7.7 0 0 1-.5-1.2C5.2 14.3 6 12.5 6 9Z" />
    <path d="M10 19.5a2.2 2.2 0 0 0 4 0" />
  </Icon>
);

/** Two arrows chasing round, for a task that repeats. Beside BellIcon for the
 *  same reason that one is beside CalendarIcon: the property sits with the
 *  dates. */
export const RepeatIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 11V9.5A3.5 3.5 0 0 1 7.5 6H19" />
    <path d="m16 3 3 3-3 3" />
    <path d="M20 13v1.5a3.5 3.5 0 0 1-3.5 3.5H5" />
    <path d="m8 21-3-3 3-3" />
  </Icon>
);

export const TagIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M11.6 3.5H4.5a1 1 0 0 0-1 1v7.1a1 1 0 0 0 .3.7l8.4 8.4a1 1 0 0 0 1.4 0l7.1-7.1a1 1 0 0 0 0-1.4L12.3 3.8a1 1 0 0 0-.7-.3Z" />
    <path d="M7.8 7.8h.01" />
  </Icon>
);

/** The one-click "advance this task" arrow in a list row. */
export const ArrowRightIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M5 12h13M13 7l5 5-5 5" />
  </Icon>
);

export const RefreshIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M3 12a9 9 0 0 1 15.3-6.4L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.3 6.4L3 16" />
    <path d="M3 21v-5h5" />
  </Icon>
);

/**
 * Moved here from StatusFilter, which had it privately, once a second popover
 * trigger wanted one. Defaults to the muted stone it has always rendered in --
 * it sits beside a button's label rather than being the button.
 */
export const ChevronIcon = ({ className = 'h-4 w-4 text-stone-400' }: { className?: string }) => (
  <Icon className={className}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);

/**
 * Left and right carets, drawn rather than a rotated `ChevronIcon`.
 *
 * BackLink's private `BackChevron` was the first thing to want a left one and
 * said why it drew its own: rotating a down caret puts a transform on a
 * decorative shape to save eight lines. These are those eight lines, in the
 * file that owns the stroke spec, and BackLink now imports the left one instead
 * of keeping the only hand-drawn glyph outside this file.
 */
export const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="m15 18-6-6 6-6" />
  </Icon>
);

export const ChevronRightIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="m9 18 6-6-6-6" />
  </Icon>
);

/** The up caret, drawn rather than a flipped `ChevronIcon` for the reason the
 *  two above are drawn: a transform on a decorative shape to save three lines. */
export const ChevronUpIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="m6 15 6-6 6 6" />
  </Icon>
);

/**
 * The drag handle: six dots, the shape everything from a spreadsheet to a phone
 * uses to say "this row can be picked up".
 *
 * Filled circles rather than strokes, which is a departure from the spec above
 * and a deliberate one -- TargetIcon already fills its inner ring for the same
 * kind of reason. The alternative within the spec is three horizontal rules,
 * and that is MenuIcon: a grip has to be distinguishable from a menu at 14px,
 * or the only affordance for dragging reads as a button that opens something.
 */
export const GripIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    {[8, 12, 16].map((y) => (
      <g key={y}>
        <circle cx="9.5" cy={y} r="1.3" fill="currentColor" stroke="none" />
        <circle cx="14.5" cy={y} r="1.3" fill="currentColor" stroke="none" />
      </g>
    ))}
  </Icon>
);


/* ---------------------------------------------------------------------------
   Navigation. Moved up from Layout, which had its own copy of the factory
   above.
   --------------------------------------------------------------------------- */

/** A house rather than a 2x2 grid, which would read as the Projects grid-view
 *  toggle. */
export const HomeIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <Icon className={className}>
    <path d="M3 10.5 12 3.5l9 7" />
    <path d="M5.5 9.5V20a.5.5 0 0 0 .5.5h12a.5.5 0 0 0 .5-.5V9.5" />
  </Icon>
);

export const FolderIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <Icon className={className}>
    <path d="M3.5 6.5a1 1 0 0 1 1-1h4l2 2.5h9a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1Z" />
  </Icon>
);

/** Two figures rather than one: a single silhouette reads as "my account",
 *  which is what the block at the bottom of the sidebar already is. */
export const PeopleIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.5a3 3 0 0 1 0 5.8" />
    <path d="M17.5 14.2a5.5 5.5 0 0 1 3 5.3" />
  </Icon>
);

export const MenuIcon = ({ className = 'h-5 w-5' }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

/* ---------------------------------------------------------------------------
   The Projects view toggle. Moved up from Projects.tsx, same reason.
   --------------------------------------------------------------------------- */

export const GridIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </Icon>
);

export const ListIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  </Icon>
);

/* ---------------------------------------------------------------------------
   Message tones. One per Alert tone, so a banner is identifiable without
   reading it -- and without relying on the red/amber/green fill alone, which
   is the one cue a colour-blind reader does not get.
   --------------------------------------------------------------------------- */

export const ErrorIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5M12 16h.01" />
  </Icon>
);

export const WarningIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M12 4.5 2.8 20h18.4L12 4.5Z" />
    <path d="M12 10v4M12 17h.01" />
  </Icon>
);

export const SuccessIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.2 2.4 2.4 4.6-4.8" />
  </Icon>
);

export const InfoIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5.5M12 8h.01" />
  </Icon>
);

/* ---------------------------------------------------------------------------
   Empty states and filters.
   --------------------------------------------------------------------------- */

export const SearchIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Icon>
);

/** The generic "there is nothing here yet" glyph: an open tray. */
export const InboxIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M3.5 13.5 6 5h12l2.5 8.5v5a1 1 0 0 1-1 1h-15a1 1 0 0 1-1-1Z" />
    <path d="M3.5 13.5H9a3 3 0 0 0 6 0h5.5" />
  </Icon>
);

/* ---------------------------------------------------------------------------
   The two project sections that are still placeholders. A glyph is what stops
   a "coming soon" box reading as an error.
   --------------------------------------------------------------------------- */

export const ChartIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 20V4M4 20h16" />
    <path d="M8.5 20v-6M13 20V8.5M17.5 20v-9" />
  </Icon>
);

export const PencilIcon = ({ className }: { className?: string }) => (
  <Icon className={className}>
    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="m14.5 6.5 3 3" />
  </Icon>
);
